import React, { useState, useEffect, useRef } from 'react';
import SoundManager from '../utils/SoundManager';

const ROMAJI_TO_KANA: Record<string, string> = {
  a: 'あ', i: 'い', u: 'う', e: 'え', o: 'お',
  ka: 'か', ki: 'き', ku: 'く', ke: 'け', ko: 'こ',
  ga: 'が', gi: 'ぎ', gu: 'ぐ', ge: 'げ', go: 'ご',
  sa: 'さ', si: 'し', shi: 'し', su: 'す', se: 'せ', so: 'そ',
  za: 'ざ', zi: 'じ', ji: 'じ', zu: 'ず', ze: 'ぜ', zo: 'ぞ',
  ta: 'た', ti: 'ち', chi: 'ち', tu: 'つ', tsu: 'つ', te: 'て', to: 'と',
  da: 'だ', di: 'ぢ', du: 'づ', de: 'で', do: 'ど',
  na: 'な', ni: 'に', nu: 'ぬ', ne: 'ね', no: 'の',
  ha: 'は', hi: 'ひ', hu: 'ふ', fu: 'ふ', he: 'へ', ho: 'ほ',
  ba: 'ば', bi: 'び', bu: 'ぶ', be: 'べ', bo: 'ぼ',
  pa: 'ぱ', pi: 'ぴ', pu: 'ぷ', pe: 'ぺ', po: 'ぽ',
  ma: 'ま', mi: 'み', mu: 'む', me: 'め', mo: 'も',
  ya: 'や', yu: 'ゆ', yo: 'よ',
  ra: 'ら', ri: 'り', ru: 'る', re: 'れ', ro: 'ろ',
  wa: 'わ', wo: 'を', n: 'ん', nn: 'ん',
  kya: 'きゃ', kyu: 'きゅ', kyo: 'きょ',
  sha: 'しゃ', shu: 'しゅ', sho: 'しょ',
  cha: 'ちゃ', chu: 'ちゅ', cho: 'ちょ',
  nya: 'にゃ', nyu: 'にゅ', nyo: 'にょ',
  hya: 'ひゃ', hyu: 'ひゅ', hyo: 'ひょ',
  mya: 'みゃ', myu: 'みゅ', myo: 'みょ',
  rya: 'りゃ', ryu: 'りゅ', ryo: 'りょ',
  gya: 'ぎゃ', gyu: 'ぎゅ', gyo: 'ぎょ',
  ja: 'じゃ', ju: 'じゅ', jo: 'じょ',
  jya: 'じゃ', jyu: 'じゅ', jyo: 'じょ',
  bya: 'びゃ', byu: 'びゅ', byo: 'びょ',
  pya: 'ぴゃ', pyu: 'ぴゅ', pyo: 'ぴょ',
};

const EXAMPLES = [
  { romaji: 'konnichiwa', kana: 'こんにちは', meaning: 'hello' },
  { romaji: 'arigatou', kana: 'ありがとう', meaning: 'thank you' },
  { romaji: 'karakoto', kana: 'からこと', meaning: 'Karakoto (place)' },
  { romaji: 'kojima', kana: 'こじま', meaning: 'Kojima (place)' },
  { romaji: 'okayama', kana: 'おかやま', meaning: 'Okayama' },
  { romaji: 'akiya', kana: 'あきや', meaning: 'abandoned house' },
  { romaji: 'sayounara', kana: 'さようなら', meaning: 'goodbye' },
];

function convertRomajiToKana(input: string): { kana: string; remainder: string } {
  let i = 0;
  let result = '';
  const lower = input.toLowerCase();

  while (i < lower.length) {
    // Try 3-char match first (sha, kyu, tsu, etc.), then 2, then 1
    let matched = false;
    for (const len of [3, 2, 1]) {
      const chunk = lower.slice(i, i + len);
      if (chunk.length === len && ROMAJI_TO_KANA[chunk]) {
        result += ROMAJI_TO_KANA[chunk];
        i += len;
        matched = true;
        break;
      }
    }
    if (!matched) {
      // Sokuon: double consonant → small tsu
      if (
        i + 1 < lower.length &&
        lower[i] === lower[i + 1] &&
        'bcdfghjkmprstvwz'.includes(lower[i])
      ) {
        result += 'っ';
        i += 1;
      } else {
        // Unknown char (punctuation, space, etc.) — keep as remainder
        return { kana: result, remainder: lower.slice(i) };
      }
    }
  }
  return { kana: result, remainder: '' };
}

const TypewriterVisualization: React.FC = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<{ romaji: string; kana: string }[]>([]);
  const [exampleIdx, setExampleIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      const { kana } = convertRomajiToKana(input.trim());
      setHistory((prev) => [...prev, { romaji: input.trim(), kana }]);
      setInput('');
      SoundManager.playSound('command');
    } else if (e.key.length === 1) {
      // Per-keystroke clack
      SoundManager.playSound('keypress');
    }
  };

  const { kana: livePreview } = convertRomajiToKana(input);

  const nextExample = () => {
    setExampleIdx((i) => (i + 1) % EXAMPLES.length);
    SoundManager.playSound('toggle');
  };

  const ex = EXAMPLES[exampleIdx];

  return (
    <div className="w-full h-full flex flex-col p-4 text-[#5fe979] font-mono">
      <div className="mb-4">
        <h3 className="text-lg font-bold mb-1">零式タイプライター</h3>
        <p className="text-xs opacity-70">
          ZERO-SHIKI TYPEWRITER — type romaji, see hiragana appear in real time.
        </p>
        <p className="text-xs opacity-70">Press ENTER to commit a word.</p>
      </div>

      {/* Input zone */}
      <div className="border border-[var(--terminal-border)] rounded p-3 mb-4 bg-black bg-opacity-50">
        <div className="text-xs opacity-60 mb-1">INPUT (romaji):</div>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full bg-transparent text-[#5fe979] outline-none text-lg font-mono"
          placeholder="konnichiwa, arigatou..."
          autoComplete="off"
          spellCheck={false}
        />
        <div className="text-xs opacity-60 mt-3 mb-1">PREVIEW (ひらがな):</div>
        <div
          className="text-3xl min-h-[3rem] text-[#7aff94]"
          style={{ fontFamily: "'Yu Gothic', 'Hiragino Sans', 'Noto Sans JP', sans-serif" }}
        >
          {livePreview || <span className="opacity-30">　</span>}
        </div>
      </div>

      {/* Example helper */}
      <div className="border border-[var(--terminal-border)] rounded p-3 mb-4 bg-black bg-opacity-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs opacity-60 mb-1">EXAMPLE #{exampleIdx + 1}/{EXAMPLES.length}:</div>
            <div className="text-sm">
              <span className="opacity-70">{ex.romaji}</span> →{' '}
              <span
                className="text-[#7aff94] text-lg"
                style={{ fontFamily: "'Yu Gothic', 'Hiragino Sans', 'Noto Sans JP', sans-serif" }}
              >
                {ex.kana}
              </span>{' '}
              <span className="opacity-50 text-xs">({ex.meaning})</span>
            </div>
          </div>
          <button
            onClick={nextExample}
            className="px-3 py-1 border border-[#5fe979] text-xs hover:bg-[#132a1b] transition-colors"
          >
            NEXT &gt;
          </button>
        </div>
      </div>

      {/* History */}
      <div className="flex-grow overflow-y-auto border border-[var(--terminal-border)] rounded p-3 bg-black bg-opacity-50">
        <div className="text-xs opacity-60 mb-2">HISTORY:</div>
        {history.length === 0 ? (
          <div className="text-xs opacity-30">// type a word and press ENTER...</div>
        ) : (
          <ul className="space-y-1">
            {history.map((h, i) => (
              <li key={i} className="text-sm">
                <span className="opacity-50">{String(i + 1).padStart(2, '0')}.</span>{' '}
                <span className="opacity-70">{h.romaji}</span>{' '}
                <span className="opacity-30">→</span>{' '}
                <span
                  className="text-[#7aff94]"
                  style={{ fontFamily: "'Yu Gothic', 'Hiragino Sans', 'Noto Sans JP', sans-serif" }}
                >
                  {h.kana}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TypewriterVisualization;
